/**
 * Décode une chaîne encodée selon l'algorithme Google Encoded Polyline
 * en un tableau de coordonnées géographiques [latitude, longitude].
 *
 * L'algorithme encode chaque coordonnée sous forme de delta signé (par rapport
 * à la coordonnée précédente), puis applique un encodage zigzag pour convertir
 * les nombres signés en entiers non-signés, avant de les découper en blocs de
 * 5 bits stockés en ASCII.
 *
 * @see https://developers.google.com/maps/documentation/utilities/polylinealgorithm
 * @param encoded - La chaîne polyline encodée
 * @returns Un tableau de paires [latitude, longitude] en degrés décimaux
 */
export function decodePolyline(encoded: string): L.LatLngExpression[] {
  const result: L.LatLngExpression[] = [];
  let index: number = 0; // position courante dans la chaîne encodée
  let lat: number = 0; // latitude accumulée (les coordonnées sont des deltas)
  let lng: number = 0; // longitude accumulée

  while (index < encoded.length) {
    let b: number;
    let shift: number = 0;
    let val: number = 0;

    // --- Décodage de la latitude ---
    // Chaque caractère est décalé de -63 pour obtenir un bloc de 6 bits.
    // Le bit de poids fort (bit 5, i.e. >= 0x20) indique qu'un autre bloc suit.
    // Les 5 bits utiles (bits 0–4) sont assemblés en LSB-first dans val.
    do {
      b = encoded.charCodeAt(index++) - 63;
      val |= (b & 0x1f) << shift; // accumule les 5 bits utiles du bloc
      shift += 5;
    } while (b >= 0x20); // tant que le bit 5 est à 1, il reste des blocs

    // val est un entier zigzag-encodé : les impairs représentent des négatifs.
    // Conversion zigzag → entier signé, puis ajout au delta courant.
    lat += val & 1 ? ~(val >> 1) : val >> 1;

    // Réinitialisation pour le décodage de la longitude
    shift = 0;
    val = 0;

    // --- Décodage de la longitude (même algorithme) ---
    do {
      b = encoded.charCodeAt(index++) - 63;
      val |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    // Conversion zigzag → entier signé, puis ajout au delta courant.
    lng += val & 1 ? ~(val >> 1) : val >> 1;

    // Les coordonnées sont stockées multipliées par 1e5 pour éviter les flottants.
    // On divise pour retrouver les degrés décimaux (ex: 4825173 → 48.25173°).
    result.push([lat / 1e5, lng / 1e5]);
  }

  return result;
}
